// @ts-nocheck

import clsx from "clsx";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper/types";

import type { MediaPostType } from "@features/media-card/model";

import { Icon } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { StyledButton } from "@shared/ui/styled-button";
import { Video } from "@shared/ui/video";
import { getMediaType } from "@shared/utils/get-media-type";

import s from "./media-popup.module.scss";

export type MediaPopupProps = ComponentProps<"div"> & {
  className?: string;
  media: MediaPostType[];
  currentId: string;
  isOpen: boolean;
};

type VideoRefType = {
  ref: HTMLVideoElement | null;
  documentId: string;
};

export const MediaPopup = (props: MediaPopupProps) => {
  const { className, media, currentId, isOpen } = props;

  const $swiperRef = useRef<SwiperCore | null>(null);
  const videoRefs = useRef<VideoRefType[]>([]);

  useEffect(() => {
    const index = media?.findIndex((post) => post.documentId === currentId);

    $swiperRef?.current?.slideTo(index);
  }, [currentId]);

  const [activeIndex, setActiveIndex] = useState(0);

  /**
   * Пока попап не открывали, слайды не монтируем. Раньше <video> для всех
   * слайдов висели в DOM у верхней кромки экрана: браузер тянул для них данные,
   * а IntersectionObserver считал их видимыми. На главной так качался ролик,
   * который пользователь мог вообще не открыть.
   */
  const [wasOpened, setWasOpened] = useState(false);

  useEffect(() => {
    if (isOpen) setWasOpened(true);
  }, [isOpen]);

  const slidePrev = useCallback(() => {
    $swiperRef?.current?.slidePrev();
  }, []);

  const slideNext = useCallback(() => {
    $swiperRef?.current?.slideNext();
  }, []);

  const stopAllVideo = useCallback(() => {
    if (videoRefs.current.length > 0) {
      videoRefs.current.forEach((item) => {
        if (item.ref) {
          item.ref.pause();
          item.ref.currentTime = 0;
        }
      });
    }
  }, []);

  useEffect(() => {
    if (videoRefs.current.length > 0) {
      const currentMediaId = media[activeIndex].documentId;

      const currentVideoRef = videoRefs.current.find(
        (item) => item?.documentId === currentMediaId,
      );

      if (currentVideoRef?.documentId && isOpen) {
        currentVideoRef?.ref?.play();
      }

      if (!isOpen || !currentVideoRef?.documentId) {
        stopAllVideo();
      }
    }
  }, [media, activeIndex, stopAllVideo, isOpen]);

  return (
    <div className={clsx(s.root, className, { [s.openPopup]: isOpen })}>
      <Swiper
        modules={[Pagination, EffectFade]}
        className={s.slider}
        loop
        effect="fade"
        fadeEffect={{ crossFade: true }}
        pagination={{
          el: `.${s.pagination}`,
          type: "bullets",
          clickable: true,
          bulletClass: `${s.bullet}`,
          bulletActiveClass: `${s.bulletActive}`,
        }}
        onSlideChange={({ realIndex }) => setActiveIndex(realIndex)}
        onBeforeInit={(swiper) => {
          $swiperRef.current = swiper;
        }}
      >
        {(wasOpened ? (media ?? []) : []).map((slide, index) => {
          const isVideo =
            getMediaType(slide?.mediaContent?.media?.mime!) === "video";

          return (
            <SwiperSlide
              key={`popup-slide-${slide?.documentId}`}
              className={clsx(s.slide, {
                [s.activeSlide]: index === activeIndex,
              })}
            >
              {isVideo ? (
                slide?.mediaContent?.media?.url ? (
                  <Video
                    src={slide.mediaContent.media.url}
                    className={s.video}
                    controls
                    ref={(ref) =>
                      // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
                      (videoRefs.current[index] = {
                        ref,
                        documentId: slide.documentId,
                      })
                    }
                  />
                ) : null
              ) : slide?.mediaContent?.media?.url ? (
                <Image
                  src={slide.mediaContent.media.url}
                  alt={
                    slide.mediaContent.media?.alternativeText ?? "media image"
                  }
                  className={s.media}
                  height="100%"
                  objectFit="contain"
                />
              ) : null}
            </SwiperSlide>
          );
        })}
        {media?.length > 1 && <div className={s.pagination}></div>}
      </Swiper>
      <div className={s.navArrows}>
        <StyledButton
          variant="primary-light"
          onClick={slidePrev}
          aria-label="prev slide"
          size="xs"
        >
          <Icon name="arrow-left" />
        </StyledButton>
        <StyledButton
          variant="primary-light"
          onClick={slideNext}
          aria-label="next slide"
          size="xs"
        >
          <Icon name="arrow-right" />
        </StyledButton>
      </div>
    </div>
  );
};

MediaPopup.displayName = "MediaPopup";
